import { useState } from "react";
import { apiJson } from "../../api/request.js";
import "../../styles/privacy.css";

export function GuestPrivacyActions(): React.JSX.Element {
  const { token, state, remove } = useGuestPrivacy();
  const [confirming, setConfirming] = useState(false);
  return (
    <GuestPrivacyControls
      token={token}
      state={state}
      remove={remove}
      confirming={confirming}
      setConfirming={setConfirming}
    />
  );
}

function useGuestPrivacy() {
  const [token] = useState(() =>
    new URLSearchParams(window.location.search).get("guestClaim"),
  );
  const [state, setState] = useState<"ready" | "busy" | "done" | "error">(
    "ready",
  );
  async function remove() {
    if (!token || state === "busy") return;
    setState("busy");
    try {
      await apiJson("/api/v1/guest-privacy/delete", {
        method: "POST",
        body: { token },
      });
      setState("done");
      const url = new URL(window.location.href);
      url.searchParams.delete("guestClaim");
      window.history.replaceState(
        null,
        "",
        url.pathname + url.search + url.hash,
      );
    } catch {
      setState("error");
    }
  }
  return { token, state, remove };
}

function GuestPrivacyControls({
  token,
  state,
  remove,
  confirming,
  setConfirming,
}: ReturnType<typeof useGuestPrivacy> & {
  confirming: boolean;
  setConfirming(value: boolean): void;
}): React.JSX.Element {
  return (
    <section className="fr-guest-privacy" aria-label="Guest privacy">
      <a
        className="fr-auth-link"
        href="/privacy"
        target={token ? "_blank" : undefined}
        rel="noopener noreferrer"
      >
        Privacy
      </a>
      {token && state === "done" ? (
        <p role="status">
          Your number has been removed from this guest record. Its SMS links no
          longer work. The shared scorecard is kept.
        </p>
      ) : null}
      {token && state !== "done" ? (
        <>
          {confirming ? (
            <>
              <p>
                Remove your number from this guest record and cancel queued
                messages? You will no longer be able to claim your rounds using
                these SMS links. The shared scores remain.
              </p>
              <div className="fr-guest-actions">
                <button
                  className="fr-btn"
                  type="button"
                  disabled={state === "busy"}
                  onClick={() => void remove()}
                >
                  {state === "busy" ? "Removing…" : "Delete my number"}
                </button>
                <button
                  className="fr-btn"
                  type="button"
                  disabled={state === "busy"}
                  onClick={() => setConfirming(false)}
                >
                  Keep my number
                </button>
              </div>
            </>
          ) : (
            <button
              className="fr-auth-link"
              type="button"
              onClick={() => setConfirming(true)}
            >
              Delete my number — no account needed
            </button>
          )}
          {state === "error" ? (
            <p role="alert">
              We could not remove the number. Try your latest SMS link or
              contact us from Privacy.
            </p>
          ) : null}
        </>
      ) : null}
    </section>
  );
}
